import type { ProjectWithPermission } from '../api';
import { ProjectCard } from '../components/ProjectCard';
import { CreateProjectCard } from '../components/CreateProjectCard';

interface ProjectSelectionPageProps {
  projects: ProjectWithPermission[];
  pageType: 'dashboard' | 'uploads';
  onSelectProject: (project: ProjectWithPermission) => void;
  canCreateProject?: boolean;
  onCreateProject?: () => void;
  /** id of the project whose external-UI handoff is being minted (spinner). */
  busyProjectId?: string | null;
}

export function ProjectSelectionPage({
  projects,
  pageType,
  onSelectProject,
  canCreateProject = false,
  onCreateProject,
  busyProjectId = null,
}: ProjectSelectionPageProps) {
  const isDashboard = pageType === 'dashboard';
  const title = isDashboard ? 'Dashboard' : 'Uploads';
  const subtitle = isDashboard
    ? 'Select a project to view your results'
    : 'Select a project to manage files';

  const handleCreate = () => {
    onCreateProject?.();
  };

  if (projects.length === 0) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{title}</h1>
          <p className="subtitle">{subtitle}</p>
        </div>
        <div className="dashboard-empty-state">
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            className="dashboard-empty-icon"
          >
            <rect x="8" y="12" width="48" height="40" rx="4" stroke="currentColor" strokeWidth="2" />
            <path d="M8 22h48" stroke="currentColor" strokeWidth="2" />
            <rect x="16" y="30" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
            <rect x="34" y="30" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          </svg>
          <h2 className="dashboard-empty-title">No projects available</h2>
          <p className="dashboard-empty-description">
            {canCreateProject
              ? 'Get started by creating your first project.'
              : isDashboard
              ? 'You don\'t have access to any projects yet. Contact your organization admin to get started.'
              : 'You don\'t have upload access to any projects yet. Contact your organization admin to get started.'}
          </p>
          {canCreateProject && onCreateProject && (
            <button type="button" className="btn-primary dashboard-empty-action" onClick={handleCreate}>
              Create project
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>{title}</h1>
        <p className="subtitle">{subtitle}</p>
      </div>
      <div className="project-grid">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={() => onSelectProject(project)}
            busy={busyProjectId === project.id}
          />
        ))}
        {canCreateProject && onCreateProject && (
          <CreateProjectCard onClick={handleCreate} />
        )}
      </div>
    </div>
  );
}
